// /pipeline (WEB_PLAN.md §5.9): port of GroundTeam/Views/Records/PipelineView.swift's pipeline board, read-only in
// Phase 1. The header carries the pipeline and owner menus (kept in the URL, so a board can be shared), the open and
// weighted value of what is on the board, and Export CSV; below it, one column per stage. The rules live in
// boardModel.ts (tested); this file wires the data hooks to it.

import { Download, SquareKanban, TriangleAlert } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { idKey } from '@/contracts/uuid';
import { byId } from '@/domain/util';
import { formatGrouped } from '@/domain/format';
import { usePipelines, useTerritories, useUsers, useViewer, useVisibleRecords } from '@/data/hooks';
import { MoneyText, RequireFirstSync, useDocumentTitle, useStoreRowLookups } from '@/features/common';
import { useCan } from '@/session/permissions';
import { Button, EmptyState, PageHeader, Skeleton, Stat, cn, useToast } from '@/ui';
import { OwnerFilterMenu, PipelinePicker } from './BoardMenus';
import { csvFileName, recordsCsv } from './boardCsv';
import {
  boardExportRecords,
  buildBoard,
  ownerFilterLabel,
  ownerOptions,
  pipelineMenuGroups,
  noPipelinesMessage,
  noStagesMessage,
  pipelineOwners,
  readBoardSelection,
  storesListHref,
  withoutStageText,
  writeBoardSelection,
} from './boardModel';
import { downloadText } from './download';
import { PipelineBoard } from './PipelineBoard';
import { WithoutStageDialog } from './WithoutStageDialog';

/** Same gutter as PageContainer, so the board lines up with every other screen; the board itself runs edge to edge. */
const GUTTER = 'px-4 sm:px-6 lg:px-8';

export default function PipelinePage() {
  useDocumentTitle('Pipeline');
  return (
    // The page fills the shell's scroll area and lets each column scroll on its own; below ~560 px of height the
    // whole page scrolls instead of squeezing the columns to nothing.
    <div className="flex h-full min-h-[560px] flex-col">
      <RequireFirstSync what="Pipeline" fallback={<BoardSkeleton />}>
        <PipelineScreen />
      </RequireFirstSync>
    </div>
  );
}

function PipelineScreen() {
  const can = useCan();
  const viewer = useViewer();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const pipelinesList = usePipelines();
  const records = useVisibleRecords();
  const users = useUsers();
  const territories = useTerritories();
  const lookups = useStoreRowLookups();
  const [showWithoutStage, setShowWithoutStage] = useState(false);

  const pipelines = pipelinesList.values;
  const viewerId = viewer?.profileId ?? null;
  const { pipeline, ownerId } = useMemo(() => readBoardSelection(params, pipelines), [params, pipelines]);

  const groups = useMemo(() => pipelineMenuGroups(pipelines), [pipelines]);
  const owners = useMemo(() => pipelineOwners(records.values, users.values), [records, users]);
  const options = useMemo(() => ownerOptions(owners, viewerId, can.isManager), [owners, viewerId, can.isManager]);
  const ownerLabel = ownerFilterLabel(ownerId, viewerId, lookups.users);

  const board = useMemo(
    () => (pipeline ? buildBoard(pipeline, records.values, pipelines, { ownerId, modifiedAt: records.modifiedAt }) : null),
    [pipeline, records, pipelines, ownerId],
  );

  const choosePipeline = useCallback(
    (pipelineId: string) => setParams((prev) => writeBoardSelection(prev, { pipelineId }), { replace: true }),
    [setParams],
  );
  const chooseOwner = useCallback(
    (owner: string | null) => setParams((prev) => writeBoardSelection(prev, { ownerId: owner }), { replace: true }),
    [setParams],
  );

  const exportCsv = useCallback(() => {
    if (!board) return;
    try {
      const text = recordsCsv(boardExportRecords(board), {
        pipelines,
        users: lookups.users,
        territories: byId(territories.values),
        visitResults: lookups.visitResults,
        stats: lookups.stats,
      });
      downloadText(csvFileName(board.pipeline.name), text);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err), { title: "Couldn't export the board" });
    }
  }, [board, pipelines, lookups, territories, toast]);

  if (!pipeline || !board) {
    return (
      <div className={cn(GUTTER, 'py-5 lg:py-6')}>
        <PageHeader title="Pipeline" />
        <EmptyState
          icon={<SquareKanban />}
          title="No pipelines"
          message={noPipelinesMessage(can.isAdmin)}
        />
      </div>
    );
  }

  const withoutStage = board.withoutStage.length;
  const storesHref = storesListHref(pipeline.id, ownerId);

  return (
    <>
      <PageHeader
        title="Pipeline"
        className={cn(GUTTER, 'pt-5 lg:pt-6')}
        actions={
          <>
            <PipelinePicker groups={groups} current={pipeline} onSelect={choosePipeline} />
            <OwnerFilterMenu options={options} ownerId={ownerId} label={ownerLabel} onSelect={chooseOwner} />
            {can.canExportCsv && (
              <Button icon={<Download />} onClick={exportCsv} disabled={board.storeCount === 0}>
                Export CSV
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <Stat label="Open pipeline" value={<MoneyText value={board.open} compact />} />
          <Stat label="Weighted" value={<MoneyText value={board.weighted} compact />} />
          <Stat label="Stores" value={formatGrouped(board.storeCount)} />
          {withoutStage > 0 && (
            <Button
              variant="ghost"
              size="sm"
              icon={<TriangleAlert className="text-warning" />}
              onClick={() => setShowWithoutStage(true)}
              aria-haspopup="dialog"
            >
              {withoutStageText(withoutStage)}
            </Button>
          )}
        </div>
      </PageHeader>

      {board.columns.length === 0 ? (
        <div className={GUTTER}>
          <EmptyState
            icon={<SquareKanban />}
            title="No stages"
            message={noStagesMessage(pipeline, can.isAdmin)}
          />
        </div>
      ) : (
        // Keyed by pipeline: another pipeline's board starts at its first stage, not scrolled to where this one was.
        <PipelineBoard
          key={idKey(pipeline.id)}
          board={board}
          itemOf={records.get}
          lookups={lookups}
          className={cn(GUTTER, 'flex-1 pb-5')}
        />
      )}

      <WithoutStageDialog
        open={showWithoutStage}
        onClose={() => setShowWithoutStage(false)}
        pipeline={pipeline}
        records={board.withoutStage}
        itemOf={records.get}
        lookups={lookups}
        storesHref={storesHref}
      />
    </>
  );
}

/** The board's outline while the replica loads: a header and four columns of card placeholders. */
function BoardSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading the pipeline…" className="flex min-h-0 flex-1 flex-col">
      <div className={cn(GUTTER, 'flex flex-col gap-4 pt-5 pb-4 lg:pt-6')}>
        <Skeleton className="h-8 w-40" />
        <div className="flex gap-8">
          <Skeleton className="h-11 w-28" />
          <Skeleton className="h-11 w-28" />
          <Skeleton className="h-11 w-20" />
        </div>
      </div>
      <div className={cn(GUTTER, 'flex min-h-0 flex-1 gap-3 overflow-hidden pb-5')}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex w-[288px] shrink-0 flex-col gap-2 rounded-card border border-line bg-surface-2 p-2">
            <Skeleton className="h-14 w-full" />
            {[0, 1, 2].map((j) => (
              <Skeleton key={j} className="h-24 w-full" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
